import React, { useState } from 'react';
import {
  IconButton, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText, Snackbar
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import XIcon from '@mui/icons-material/X';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GroupsIcon from '@mui/icons-material/Groups';
import EmailIcon from '@mui/icons-material/Email';
import LinkIcon from '@mui/icons-material/Link';
import IosShareIcon from '@mui/icons-material/IosShare';

/**
 * ShareButton — share the current page on social media / Teams / email.
 *
 * On devices with the Web Share API (mostly mobile), the primary click
 * opens the native share sheet. Otherwise it opens a menu of share targets.
 *
 * @param {Object} props
 * @param {string} [props.title] - Share text. Defaults to document.title.
 * @param {string} [props.url]   - URL to share. Defaults to current page (no query).
 * @param {string} [props.size]  - IconButton size. Default 'small'.
 */
export default function ShareButton({ title, url, size = 'small' }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const open = Boolean(anchorEl);

  const shareUrl = url || window.location.href.split('?')[0];
  const shareTitle = title || (typeof document !== 'undefined' && document.title) || 'ParlSearch';

  const hasWebShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const u = encodeURIComponent(shareUrl);
  const t = encodeURIComponent(shareTitle);

  const targets = [
    {
      key: 'x',
      label: 'Share on X',
      icon: <XIcon fontSize="small" />,
      href: `https://twitter.com/intent/tweet?text=${t}&url=${u}`,
    },
    {
      key: 'facebook',
      label: 'Share on Facebook',
      icon: <FacebookIcon fontSize="small" />,
      href: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
    },
    {
      key: 'linkedin',
      label: 'Share on LinkedIn',
      icon: <LinkedInIcon fontSize="small" />,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
    },
    {
      key: 'teams',
      label: 'Share to Microsoft Teams',
      icon: <GroupsIcon fontSize="small" />,
      href: `https://teams.microsoft.com/share?href=${u}&msgText=${t}`,
    },
    {
      key: 'email',
      label: 'Share via email',
      icon: <EmailIcon fontSize="small" />,
      href: `mailto:?subject=${t}&body=${t}%20${u}`,
    },
  ];

  const closeMenu = () => setAnchorEl(null);

  const handleButtonClick = async (event) => {
    if (hasWebShare) {
      try {
        await navigator.share({ title: shareTitle, url: shareUrl });
        return;
      } catch (err) {
        // User cancelled (AbortError) or share failed — fall back to the menu.
        if (err && err.name === 'AbortError') return;
      }
    }
    setAnchorEl(event.currentTarget);
  };

  const handleTargetClick = (href) => {
    if (href.startsWith('mailto:')) {
      window.location.href = href;
    } else {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
    closeMenu();
  };

  const handleNativeShare = async () => {
    closeMenu();
    if (hasWebShare) {
      try {
        await navigator.share({ title: shareTitle, url: shareUrl });
      } catch (err) {
        /* cancelled or unsupported — nothing to do */
      }
    }
  };

  const handleCopyLink = async () => {
    closeMenu();
    try {
      await navigator.clipboard.writeText(shareUrl);
      setSnackbarOpen(true);
    } catch (err) {
      const ta = document.createElement('textarea');
      ta.value = shareUrl;
      ta.style.position = 'fixed';
      ta.style.left = '-999999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setSnackbarOpen(true);
    }
  };

  return (
    <>
      <Tooltip title="Share">
        <IconButton
          onClick={handleButtonClick}
          size={size}
          sx={{
            color: 'text.secondary',
            '&:hover': { backgroundColor: 'action.hover' },
          }}
          aria-label="Share this page"
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <ShareIcon />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {hasWebShare && (
          <MenuItem onClick={handleNativeShare}>
            <ListItemIcon><IosShareIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Share…</ListItemText>
          </MenuItem>
        )}
        {targets.map((tg) => (
          <MenuItem key={tg.key} onClick={() => handleTargetClick(tg.href)}>
            <ListItemIcon>{tg.icon}</ListItemIcon>
            <ListItemText>{tg.label}</ListItemText>
          </MenuItem>
        ))}
        <MenuItem onClick={handleCopyLink}>
          <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Copy link</ListItemText>
        </MenuItem>
      </Menu>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message="Link copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
